/**
 * Talking to the Casa BAS browser extension.
 *
 * The extension reads Xero's own Activity Statement from Xero's website, using
 * the staff member's Xero sign-in in this browser, and hands back the figures
 * and Xero's Excel export. Chrome delivers these messages only between this
 * site and the extension with this id (its manifest lists the BAS sites).
 *
 * The id is fixed by the public key in the extension's manifest, so it is the
 * same for every install.
 */
import type { XeroFigures } from "./api";

/** The build loaded from the zip (Tools tab), fixed by the manifest's key. */
export const EXTENSION_ID = "feoediehmngkjeikbplhmepgkjpbcdgi";

/**
 * The Chrome Web Store build. The store gives an extension its own id on the
 * first upload; put it here once known. Empty until then.
 */
export const STORE_EXTENSION_ID = "";

/** Every build BAS may talk to, store first. */
export const EXTENSION_IDS = [STORE_EXTENSION_ID, EXTENSION_ID].filter(Boolean);

/** The id that last answered, so later messages go straight to it. */
let activeId: string | null = null;

interface ChromeRuntime {
  sendMessage(id: string, message: unknown, callback: (reply: unknown) => void): void;
  lastError?: { message?: string };
}

function runtime(): ChromeRuntime | null {
  const w = window as unknown as { chrome?: { runtime?: ChromeRuntime } };
  return w.chrome?.runtime?.sendMessage ? w.chrome.runtime : null;
}

function sendTo<T>(id: string, message: unknown, timeoutMs: number): Promise<T | null> {
  const rt = runtime();
  if (!rt) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(null), timeoutMs);
    try {
      rt.sendMessage(id, message, (reply) => {
        window.clearTimeout(timer);
        // lastError is set when that extension is not installed; reading it
        // stops Chrome logging it as unchecked.
        if (rt.lastError) resolve(null);
        else resolve((reply as T) ?? null);
      });
    } catch {
      window.clearTimeout(timer);
      resolve(null);
    }
  });
}

async function send<T>(message: unknown, timeoutMs: number): Promise<T | null> {
  if (activeId) return sendTo<T>(activeId, message, timeoutMs);
  for (const id of EXTENSION_IDS) {
    // Finding the build is quick; only the found one gets the long timeout.
    // In order on purpose: the store build wins when both are installed.
    // oxlint-disable-next-line no-await-in-loop
    const hello = await sendTo<{ ok?: boolean }>(id, { cmd: "hello" }, 2_000);
    if (hello?.ok) {
      activeId = id;
      return sendTo<T>(id, message, timeoutMs);
    }
  }
  return null;
}

/** The installed extension's version, or null when it is not installed (or not Chrome). */
export async function extensionVersion(): Promise<string | null> {
  const reply = await send<{ ok: boolean; version?: string }>({ cmd: "hello" }, 2_000);
  return reply?.ok ? (reply.version ?? "") : null;
}

export interface FetchedStatement {
  figures: XeroFigures;
  /** Xero's Excel export, or null when only the figures came through. */
  file: { dataUrl: string; filename: string | null; bytes: number } | null;
  /** Why the file is missing, when it is. */
  fileError: string | null;
  fetchedAt: string;
}

export type FetchResult =
  | { ok: true; statement: FetchedStatement }
  | { ok: false; message: string; signedOut?: boolean };

/** Ask the extension for one client's statement. Takes up to a few minutes. */
export async function fetchStatement(request: {
  shortcode: string;
  name: string;
  period: { start: string; end: string };
}): Promise<FetchResult> {
  const reply = await send<{
    ok: boolean;
    message?: string;
    signedOut?: boolean;
    figures?: XeroFigures;
    file?: FetchedStatement["file"];
    fileError?: string | null;
    fetchedAt?: string;
  }>({ cmd: "fetch", ...request }, 240_000);
  if (!reply) {
    return { ok: false, message: "The Casa extension did not answer. Check it is installed and enabled, then try again." };
  }
  if (!reply.ok || !reply.figures) {
    return { ok: false, message: reply.message || "The extension could not fetch the statement.", signedOut: reply.signedOut };
  }
  return {
    ok: true,
    statement: { figures: reply.figures, file: reply.file ?? null, fileError: reply.fileError ?? null, fetchedAt: reply.fetchedAt || "" },
  };
}

/** The base64 part of a data: URL. */
export function base64Of(dataUrl: string): string {
  const comma = dataUrl.indexOf(",");
  return comma >= 0 ? dataUrl.slice(comma + 1) : "";
}

/** A data: URL as a Blob, for saving the Excel file. */
export function blobOf(dataUrl: string): Blob {
  const [head, data] = [dataUrl.slice(0, dataUrl.indexOf(",")), base64Of(dataUrl)];
  const type = /data:([^;]+)/.exec(head)?.[1] || "application/octet-stream";
  const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type });
}

/**
 * Give the extension BAS's client list, so its own list needs no typing.
 * Returns how many it now holds from BAS, or null when it is not installed.
 */
export async function syncClients(clients: { shortcode: string; name: string }[]): Promise<number | null> {
  const reply = await send<{ ok: boolean; count?: number }>({ cmd: "syncClients", clients }, 5_000);
  return reply?.ok ? (reply.count ?? clients.length) : null;
}

/**
 * What kind of file a data: URL holds, from its first bytes. Names the kind
 * only, never the content, so it can go in an error message.
 */
export function fileKind(dataUrl: string): { kind: string; mime: string; bytes: number } {
  const mime = /^data:([^;,]*)/.exec(dataUrl)?.[1] || "unknown type";
  let head = "";
  let bytes = 0;
  try {
    const raw = atob(base64Of(dataUrl));
    bytes = raw.length;
    head = raw.slice(0, 8);
  } catch {
    return { kind: "unreadable data", mime, bytes };
  }
  const kind = head.startsWith("PK\u0003\u0004")
    ? "an Excel workbook (.xlsx)"
    : head.startsWith("\u00d0\u00cf\u0011\u00e0")
      ? "an older Excel file (.xls)"
      : /^\s*[{[]/.test(head)
        ? "JSON"
        : /^\s*</.test(head)
          ? "HTML or XML"
          : bytes === 0
            ? "an empty file"
            : "text or an unknown format";
  return { kind, mime, bytes };
}
